import { Outlet, useLocation, useMatches } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { TopBar } from './components/layout/TopBar';
import { ChatbotWidget } from './components/ChatbotWidget';
import { AppTooltip } from './components/AppTooltip';
import { LoginPage } from './components/auth/LoginPage';
import { discoveredModules, type ModuleRoute } from './routes/modules';
import { tenant } from './lib/tenant';
import { useAuth } from './lib/auth-context';
import { reinitialiserRechargement } from './lib/chunk-reload';
import { useRestaurationDefilement } from './lib/useRestaurationDefilement';
import { useEntetesCollants } from './lib/useEntetesCollants';

/**
 * Layout principal :
 *   ┌─────────────────────────────────────────────────┐
 *   │ TopBar (2 lignes : brand+actions, tabs modules) │
 *   ├─────────────────────────────────────────────────┤
 *   │ Outlet (route enfant) — PLEINE LARGEUR          │
 *   └─────────────────────────────────────────────────┘
 * Pas de sidebar gauche (décision Ben 17/07 : la navigation vit dans la
 * TopBar, la largeur sert aux tableaux denses type proposition tisanes).
 * Le chatbot widget est injecté en overlay sur toutes les pages.
 *
 * Garde de navigation (`CONTRACT-AUTHENTIFICATION.md` §6ter) : tant que la
 * session n'est pas établie, on affiche l'écran de connexion et RIEN
 * d'autre — pas de TopBar, pas d'Outlet, pas de chatbot.
 */
export default function App(): JSX.Element {
  const { status, utilisateur, logout } = useAuth();
  const location = useLocation();
  const matches = useMatches();
  // Retour sur un écran (Précédent, F5, retour d'un site externe) : on
  // retrouve l'endroit où l'on était dans la liste (Ben 23/09).
  useRestaurationDefilement();
  // En-têtes de colonnes collants sous la TopBar, tous tableaux (Ben 22/10).
  useEntetesCollants(status !== 'loading' && status !== 'unauthenticated' && Boolean(utilisateur));

  const activeModule = useMemo<ModuleRoute | null>(() => {
    const firstSegment = location.pathname.split('/').filter(Boolean)[0];
    if (!firstSegment) return null;
    return discoveredModules.find((m) => m.path.split('/')[0] === firstSegment) ?? null;
  }, [location.pathname]);

  const pageContext = useMemo(
    () => ({
      pathname: location.pathname,
      moduleId: activeModule?.path ?? null,
      moduleLabel: activeModule?.label ?? 'Tableau de bord',
      matches: matches.map((m) => m.pathname),
    }),
    [location.pathname, activeModule, matches],
  );

  // `App.tsx` n'est jamais lazy (pas de chunk séparé) : l'atteindre prouve
  // que le bundle courant est sain — un éventuel incident de chunk manquant
  // (`lib/chunk-reload.ts`) est résolu, la prochaine auto-récupération doit
  // repartir de zéro (ex. le redéploiement suivant, même onglet resté ouvert).
  useEffect(() => {
    reinitialiserRechargement();
  }, []);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" role="status" aria-live="polite">
        <span className="text-sm text-slate-400">Chargement…</span>
      </div>
    );
  }

  if (status === 'unauthenticated' || !utilisateur) {
    return <LoginPage />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <TopBar
        modules={discoveredModules}
        activeModulePath={activeModule?.path ?? null}
        utilisateur={utilisateur}
        onLogout={logout}
      />
      <main className="flex-1 min-w-0 min-h-0 flex flex-col">
        <Outlet />
      </main>
      <ChatbotWidget tenantId={tenant.id} pageContext={pageContext} />
      {/* Bulles d'aide stylées globales — upgrade tous les `title` natifs */}
      <AppTooltip />
    </div>
  );
}
